import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { FormsModule } from '@angular/forms';
import { ChannelMember } from '../../shared/models/chat.models';
import { ChannelAvatarImageDirective } from '../../shared/directives/channel-avatar-image.directive';
import { ChatService } from '../../shared/services/chat.service';
import { highlightMentions } from '../utils/mention-highlight';

export interface MessageSentPayload {
  text: string;
  mentions?: number[];
  attachmentUrl?: string;
  attachmentType?: string;
  attachmentName?: string;
}

@Component({
  selector: 'app-message-input',
  templateUrl: './message-input.component.html',
  styleUrls: ['./message-input.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, FormsModule, ChannelAvatarImageDirective],
})
export class MessageInputComponent implements OnChanges {
  @Input() channelId!: number;
  @Input() members: ChannelMember[] = [];
  @Output() messageSent = new EventEmitter<MessageSentPayload>();
  @Output() typing = new EventEmitter<boolean>();
  @ViewChild('textArea') textAreaRef!: ElementRef<HTMLTextAreaElement>;
  @ViewChild('highlightLayer') highlightLayerRef?: ElementRef<HTMLDivElement>;
  @ViewChild('fileInput') fileInputRef!: ElementRef<HTMLInputElement>;

  text = '';
  uploading = false;
  pendingAttachment: { url: string; type: string; name: string } | null = null;
  mentionSuggestions: ChannelMember[] = [];
  mentionQuery = '';
  mentionStart = -1;
  pendingMentions: number[] = [];
  private typingTimer: any;
  private isTyping = false;

  constructor(
    private chatService: ChatService,
    private cdr: ChangeDetectorRef,
    private sanitizer: DomSanitizer
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['channelId'] && !changes['channelId'].firstChange) {
      this.resetComposer();
    }
  }

  /** Orange @mentions behind the transparent textarea. */
  get composerHighlightHtml(): SafeHtml {
    const names = this.members
      .filter((m) => this.pendingMentions.includes(m.user_id))
      .map((m) => m.user_name);
    return this.sanitizer.bypassSecurityTrustHtml(
      highlightMentions(this.text, names)
    );
  }

  getMentionAvatarUrl(member: ChannelMember): string | null {
    return member?.profile_pic_url || member?.profile_pic || null;
  }

  onAvatarError(event: Event): void {
    const img = event.target as HTMLImageElement;
    img.style.display = 'none';

    const avatar = img.parentElement as HTMLElement;
    avatar.style.backgroundColor = '#ee7444';
  }
  
  private resetComposer(): void {
    clearTimeout(this.typingTimer);

    if (this.isTyping) {
      this.isTyping = false;
      this.typing.emit(false);
    }

    this.text = '';
    this.pendingAttachment = null;
    this.pendingMentions = [];
    this.mentionSuggestions = [];
    this.mentionQuery = '';
    this.mentionStart = -1;

    if (this.textAreaRef?.nativeElement) {
      this.textAreaRef.nativeElement.value = '';
      this.textAreaRef.nativeElement.style.height = 'auto';
    }

    this.cdr.markForCheck();
  }

  onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.send();
    }
  }

  onInput(event: Event): void {
    const el = event.target as HTMLTextAreaElement;
    this.text = el.value;
    this.pruneMentionsNotInText();
    this.checkMentionTrigger(el);
    this.autoResize(el);
    this.syncHighlightScroll();

    if (!this.isTyping && this.text.trim().length > 0) {
      this.isTyping = true;
      this.typing.emit(true);
    }

    clearTimeout(this.typingTimer);

    this.typingTimer = setTimeout(() => {
      if (this.isTyping) {
        this.isTyping = false;
        this.typing.emit(false);
      }
    }, 2000);
  }

  syncHighlightScroll(): void {
    const textarea = this.textAreaRef?.nativeElement;
    const highlight = this.highlightLayerRef?.nativeElement;
    if (!textarea || !highlight) return;
    highlight.scrollTop = textarea.scrollTop;
    highlight.scrollLeft = textarea.scrollLeft;
  }

  private checkMentionTrigger(el: HTMLTextAreaElement): void {
    const pos = el.selectionStart;
    const before = el.value.substring(0, pos);
    const atIdx = before.lastIndexOf('@');
    if (atIdx >= 0 && !before.substring(atIdx + 1).includes(' ')) {
      this.mentionStart = atIdx;
      this.mentionQuery = before.substring(atIdx + 1).toLowerCase();
      this.mentionSuggestions = this.members.filter((m) =>
        m.user_name.toLowerCase().includes(this.mentionQuery)
      );
    } else {
      this.mentionSuggestions = [];
      this.mentionStart = -1;
    }
    this.cdr.markForCheck();
  }

  selectMention(member: ChannelMember): void {
    if (this.mentionStart < 0) return;
    const before = this.text.substring(0, this.mentionStart);
    const after = this.text.substring(
      this.textAreaRef.nativeElement.selectionStart
    );
    this.text = `${before}@${member.user_name} ${after}`;
    if (!this.pendingMentions.includes(member.user_id)) {
      this.pendingMentions.push(member.user_id);
    }
    this.mentionSuggestions = [];
    this.mentionStart = -1;

    setTimeout(() => {
      const el = this.textAreaRef?.nativeElement;
      if (!el) return;
      el.value = this.text;
      const caret = before.length + member.user_name.length + 2;
      el.setSelectionRange(caret, caret);
      el.focus();
      this.autoResize(el);
      this.syncHighlightScroll();
      this.cdr.markForCheck();
    }, 0);

    this.cdr.markForCheck();
  }

  private pruneMentionsNotInText(): void {
    this.pendingMentions = this.pendingMentions.filter((id) => {
      const name = this.members.find((m) => m.user_id === id)?.user_name;
      return !!name && this.text.includes(`@${name}`);
    });
  }

  send(): void {
    if (this.uploading) return;
    const trimmed = this.text.trim();
    if (!trimmed && !this.pendingAttachment) return;

    clearTimeout(this.typingTimer);
    if (this.isTyping) {
      this.isTyping = false;
      this.typing.emit(false);
    }

    this.pruneMentionsNotInText();

    this.messageSent.emit({
      text: trimmed || (this.pendingAttachment?.name ?? ''),
      mentions:
        this.pendingMentions.length > 0 ? [...this.pendingMentions] : undefined,
      attachmentUrl: this.pendingAttachment?.url ?? undefined,
      attachmentType: this.pendingAttachment?.type ?? undefined,
      attachmentName: this.pendingAttachment?.name ?? undefined,
    });

    this.text = '';
    this.pendingAttachment = null;
    this.pendingMentions = [];
    this.mentionSuggestions = [];
    if (this.textAreaRef?.nativeElement) {
      this.textAreaRef.nativeElement.value = '';
      this.textAreaRef.nativeElement.style.height = 'auto';
    }
    this.cdr.markForCheck();
  }

  triggerFileInput(): void {
    this.fileInputRef?.nativeElement.click();
  }

  onFileSelected(event: Event): void {
    const files = (event.target as HTMLInputElement).files;
    if (!files || files.length === 0) return;
    const file = files[0];
    this.uploading = true;
    this.cdr.markForCheck();

    this.chatService.uploadAttachment(file, this.channelId).subscribe({
      next: (res) => {
        this.pendingAttachment = {
          url: res.object_key,
          type: file.type,
          name: file.name,
        };
        this.uploading = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.uploading = false;
        this.cdr.markForCheck();
      },
    });
    (event.target as HTMLInputElement).value = '';
  }

  removePendingAttachment(): void {
    this.pendingAttachment = null;
    this.cdr.markForCheck();
  }

  private autoResize(el: HTMLTextAreaElement): void {
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 160) + 'px';
    const highlight = this.highlightLayerRef?.nativeElement;
    if (highlight) {
      highlight.style.height = el.style.height;
    }
  }
}
